import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { rupees, formatIndian } from "@/lib/utils";
import { FileSpreadsheet, Loader2, Package, Gem, Coins, TrendingUp, ChevronDown, ChevronUp } from "lucide-react";

const STATE_KEY = "ceo_reports_filters";

function loadInitialFilters() {
  try {
    const raw = sessionStorage.getItem(STATE_KEY);
    return raw ? JSON.parse(raw) : { scope: "all", franchise_id: "", distributor_id: "", stock_type: "both" };
  } catch {
    return { scope: "all", franchise_id: "", distributor_id: "", stock_type: "both" };
  }
}

function SortableTh({ label, name, sort, setSort, align = "right" }) {
  const active = sort.key === name;
  const dir = active ? sort.dir : null;
  const next = () => setSort({ key: name, dir: active && dir === "desc" ? "asc" : "desc" });
  return (
    <th onClick={next}
        data-testid={`ceo-th-${name}`}
        className={`px-3 py-2 text-${align} text-[10px] uppercase tracking-wider text-slate-500 cursor-pointer select-none hover:text-[#0D1B2A]`}>
      <span className="inline-flex items-center gap-1">
        {label}
        {active ? (dir === "desc" ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />) : null}
      </span>
    </th>
  );
}

function sortRows(rows, sort) {
  if (!sort.key) return rows;
  const dir = sort.dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = a[sort.key]; const bv = b[sort.key];
    if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
    return String(av || "").localeCompare(String(bv || "")) * dir;
  });
}

function KpiCard({ icon: Icon, label, value, sublines = [], testid, accent = false }) {
  return (
    <div data-testid={testid} className={`luxe-card p-5 ${accent ? "border-l-4 border-[#C9A84C]" : ""}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
        {Icon ? <Icon className="w-4 h-4 text-[#C9A84C]" strokeWidth={1.5} /> : null}
      </div>
      <div className="font-serif text-3xl text-[#0D1B2A] tabular-nums">{value}</div>
      {sublines.length > 0 && (
        <div className="mt-2 space-y-0.5">
          {sublines.map((line, i) => (
            <div key={`${label}-${i}`} className="text-xs text-slate-500 tabular-nums">{line}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function StockValueTable({ vs }) {
  const rows = [
    { label: "Metal Cost (total)", k: "metal_cost" },
    { label: "Labour (total)", k: "labour" },
    { label: "Diamond Cost (total)", k: "diamond_cost" },
  ];
  return (
    <div className="luxe-card p-6 overflow-x-auto" data-testid="ceo-stock-value-table">
      <h3 className="font-serif text-xl text-[#0D1B2A] mb-4">Stock Value Summary</h3>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-[#0D1B2A]/10">
            <th className="py-3 pr-3">Component</th>
            <th className="py-3 px-3 text-right">Company Cost</th>
            <th className="py-3 px-3 text-right">Franchise Price</th>
            <th className="py-3 px-3 text-right bg-[#C9A84C]/5 border-l-2 border-[#C9A84C]">Customer MRP</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.k} className="border-t border-[#0D1B2A]/5">
              <td className="py-2.5 pr-3 text-slate-600">{r.label}</td>
              <td className="py-2.5 px-3 text-right tabular-nums">{rupees(vs[r.k].company)}</td>
              <td className="py-2.5 px-3 text-right tabular-nums">{rupees(vs[r.k].franchise)}</td>
              <td className="py-2.5 px-3 text-right tabular-nums bg-[#C9A84C]/5 border-l-2 border-[#C9A84C]">{rupees(vs[r.k].customer)}</td>
            </tr>
          ))}
          <tr className="border-t border-[#0D1B2A]/10 bg-[#FAF8F3]">
            <td className="py-2.5 pr-3 text-slate-600 font-medium">Sub-total</td>
            <td className="py-2.5 px-3 text-right tabular-nums">{rupees(vs.subtotal.company)}</td>
            <td className="py-2.5 px-3 text-right tabular-nums">{rupees(vs.subtotal.franchise)}</td>
            <td className="py-2.5 px-3 text-right tabular-nums bg-[#C9A84C]/5 border-l-2 border-[#C9A84C]">{rupees(vs.subtotal.customer)}</td>
          </tr>
          <tr className="border-t border-[#0D1B2A]/5">
            <td className="py-2.5 pr-3 text-slate-600">GST</td>
            <td className="py-2.5 px-3 text-right text-slate-300">—</td>
            <td className="py-2.5 px-3 text-right text-slate-300">—</td>
            <td className="py-2.5 px-3 text-right tabular-nums bg-[#C9A84C]/5 border-l-2 border-[#C9A84C]">{rupees(vs.gst.customer)}</td>
          </tr>
          <tr className="border-t-2 border-[#0D1B2A]/30 bg-[#0D1B2A]/5">
            <td className="py-3 pr-3 font-serif text-[#0D1B2A] uppercase tracking-wider text-xs">Total Value</td>
            <td className="py-3 px-3 text-right tabular-nums font-bold text-[#0D1B2A]">{rupees(vs.total.company)}</td>
            <td className="py-3 px-3 text-right tabular-nums font-bold text-[#0D1B2A]">{rupees(vs.total.franchise)}</td>
            <td className="py-3 px-3 text-right tabular-nums font-bold text-[#0D1B2A] bg-[#C9A84C]/10 border-l-2 border-[#C9A84C]">{rupees(vs.total.customer)}</td>
          </tr>
          <tr className="border-t border-[#0D1B2A]/5">
            <td className="py-2.5 pr-3 text-slate-600">Avg Diamond Rate/ct</td>
            <td className="py-2.5 px-3 text-right tabular-nums">{rupees(vs.avg_dia_rate.company)}/ct</td>
            <td className="py-2.5 px-3 text-right tabular-nums">{rupees(vs.avg_dia_rate.franchise)}/ct</td>
            <td className="py-2.5 px-3 text-right tabular-nums bg-[#C9A84C]/5 border-l-2 border-[#C9A84C]">{rupees(vs.avg_dia_rate.customer)}/ct</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function BreakdownTable({ title, rows, keyName, keyLabel, includeFranchiseCols = false, testid, defaultSort }) {
  const [sort, setSort] = useState(defaultSort);
  const sorted = useMemo(() => sortRows(rows, sort), [rows, sort]);
  const totals = useMemo(() => rows.reduce((acc, r) => ({
    pieces: acc.pieces + r.pieces,
    memo_pieces: acc.memo_pieces + (r.memo_pieces || 0),
    sold_pieces: acc.sold_pieces + (r.sold_pieces || 0),
    net_weight_g: acc.net_weight_g + r.net_weight_g,
    diamond_weight_ct: acc.diamond_weight_ct + r.diamond_weight_ct,
    company_cost: acc.company_cost + r.company_cost,
    franchise_price: acc.franchise_price + r.franchise_price,
    customer_mrp: acc.customer_mrp + r.customer_mrp,
    company_gp: acc.company_gp + r.company_gp,
  }), { pieces: 0, memo_pieces: 0, sold_pieces: 0, net_weight_g: 0, diamond_weight_ct: 0, company_cost: 0, franchise_price: 0, customer_mrp: 0, company_gp: 0 }), [rows]);
  const totalGpPct = totals.franchise_price > 0 ? (totals.company_gp / totals.franchise_price * 100) : 0;
  return (
    <div className="luxe-card p-6 overflow-x-auto" data-testid={testid}>
      <h3 className="font-serif text-xl text-[#0D1B2A] mb-4">{title}</h3>
      {rows.length === 0 ? (
        <div className="py-8 text-center text-sm text-slate-400">No data for the selected filters.</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#0D1B2A]/10">
              <SortableTh label={keyLabel} name={keyName} sort={sort} setSort={setSort} align="left" />
              <SortableTh label="Pieces" name="pieces" sort={sort} setSort={setSort} />
              {includeFranchiseCols && <SortableTh label="Memo" name="memo_pieces" sort={sort} setSort={setSort} />}
              {includeFranchiseCols && <SortableTh label="Sold" name="sold_pieces" sort={sort} setSort={setSort} />}
              <SortableTh label="Net Wt (g)" name="net_weight_g" sort={sort} setSort={setSort} />
              <SortableTh label="Dia Wt (ct)" name="diamond_weight_ct" sort={sort} setSort={setSort} />
              <SortableTh label="Company Cost" name="company_cost" sort={sort} setSort={setSort} />
              <SortableTh label="Franchise Price" name="franchise_price" sort={sort} setSort={setSort} />
              <SortableTh label="Customer MRP" name="customer_mrp" sort={sort} setSort={setSort} />
              <SortableTh label="Company GP" name="company_gp" sort={sort} setSort={setSort} />
              <SortableTh label="GP %" name="company_gp_pct" sort={sort} setSort={setSort} />
              {!includeFranchiseCols && <SortableTh label="Avg Dia Rate/ct" name="avg_dia_rate_fr" sort={sort} setSort={setSort} />}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r[keyName]} className="border-b border-[#0D1B2A]/5 hover:bg-[#FAF8F3]/40">
                <td className="px-3 py-2.5 font-medium text-[#0D1B2A]">{r[keyName]}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{formatIndian(r.pieces)}</td>
                {includeFranchiseCols && <td className="px-3 py-2.5 text-right text-xs italic text-slate-500 tabular-nums">{formatIndian(r.memo_pieces || 0)}</td>}
                {includeFranchiseCols && <td className="px-3 py-2.5 text-right text-xs italic text-slate-500 tabular-nums">{formatIndian(r.sold_pieces || 0)}</td>}
                <td className="px-3 py-2.5 text-right tabular-nums">{formatIndian(r.net_weight_g, { decimals: 2 })}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{formatIndian(r.diamond_weight_ct, { decimals: 3 })}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{rupees(r.company_cost)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{rupees(r.franchise_price)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{rupees(r.customer_mrp)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{rupees(r.company_gp)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-[#C9A84C] font-medium">{r.company_gp_pct.toFixed(1)}%</td>
                {!includeFranchiseCols && <td className="px-3 py-2.5 text-right tabular-nums">{rupees(r.avg_dia_rate_fr)}/ct</td>}
              </tr>
            ))}
            <tr className="border-t-2 border-[#C9A84C]/40 bg-[#0D1B2A]/5 font-medium italic">
              <td className="px-3 py-3 text-[#0D1B2A] uppercase tracking-wider text-xs">Total</td>
              <td className="px-3 py-3 text-right tabular-nums">{formatIndian(totals.pieces)}</td>
              {includeFranchiseCols && <td className="px-3 py-3 text-right tabular-nums">{formatIndian(totals.memo_pieces)}</td>}
              {includeFranchiseCols && <td className="px-3 py-3 text-right tabular-nums">{formatIndian(totals.sold_pieces)}</td>}
              <td className="px-3 py-3 text-right tabular-nums">{formatIndian(totals.net_weight_g, { decimals: 2 })}</td>
              <td className="px-3 py-3 text-right tabular-nums">{formatIndian(totals.diamond_weight_ct, { decimals: 3 })}</td>
              <td className="px-3 py-3 text-right tabular-nums">{rupees(totals.company_cost)}</td>
              <td className="px-3 py-3 text-right tabular-nums">{rupees(totals.franchise_price)}</td>
              <td className="px-3 py-3 text-right tabular-nums">{rupees(totals.customer_mrp)}</td>
              <td className="px-3 py-3 text-right tabular-nums">{rupees(totals.company_gp)}</td>
              <td className="px-3 py-3 text-right tabular-nums text-[#C9A84C]">{totalGpPct.toFixed(1)}%</td>
              {!includeFranchiseCols && <td />}
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function CeoReports({ franchises, distributors = [] }) {
  const [filters, setFilters] = useState(loadInitialFilters);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [report, setReport] = useState(null);

  useEffect(() => { sessionStorage.setItem(STATE_KEY, JSON.stringify(filters)); }, [filters]);

  const buildParams = () => {
    const params = new URLSearchParams({ scope: filters.scope, stock_type: filters.stock_type });
    if (filters.scope === "franchise" && filters.franchise_id) params.set("franchise_id", filters.franchise_id);
    if (filters.scope === "distributor" && filters.distributor_id) params.set("distributor_id", filters.distributor_id);
    return params;
  };

  const validateScope = () => {
    if (filters.scope === "franchise" && !filters.franchise_id) { toast.error("Please pick a franchise"); return false; }
    if (filters.scope === "distributor" && !filters.distributor_id) { toast.error("Please pick a distributor"); return false; }
    return true;
  };

  const generate = async () => {
    if (!validateScope()) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/ceo?${buildParams().toString()}`);
      setReport(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  const exportXlsx = async () => {
    if (!validateScope()) return;
    setExporting(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/ceo/export?${buildParams().toString()}`;
      const tok = localStorage.getItem("access_token");
      const r = await fetch(url, { headers: tok ? { Authorization: `Bearer ${tok}` } : {} });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const cd = r.headers.get("content-disposition") || "";
      const m = /filename=([^;]+)/i.exec(cd);
      const fname = m ? m[1].trim() : `CEO_Report_${Date.now()}.xlsx`;
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = fname; a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Exported");
    } catch (e) {
      toast.error(e.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const s = report?.summary;
  const purityLine = s ? Object.entries(s.purity_breakdown_weight || {})
    .sort((a, b) => b[1] - a[1])
    .map(([k, v]) => `${k}: ${formatIndian(v, { decimals: 2 })}g`).join(" · ") : "";

  return (
    <div className="space-y-6" data-testid="ceo-reports-pane">
      <div className="luxe-card p-6 space-y-5">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#C9A84C] mb-1">Master Admin Only</p>
          <h2 className="font-serif text-2xl text-[#0D1B2A]">Strategic Stock & Margin Overview</h2>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Scope</div>
            <div className="flex gap-4 flex-wrap">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="radio" name="scope" value="all" checked={filters.scope === "all"}
                  onChange={() => setFilters({ ...filters, scope: "all" })}
                  data-testid="ceo-scope-all" />
                <span className="text-sm">Entire Stock</span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="radio" name="scope" value="franchise" checked={filters.scope === "franchise"}
                  onChange={() => setFilters({ ...filters, scope: "franchise" })}
                  data-testid="ceo-scope-franchise" />
                <span className="text-sm">By Franchise</span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="radio" name="scope" value="distributor" checked={filters.scope === "distributor"}
                  onChange={() => setFilters({ ...filters, scope: "distributor" })}
                  data-testid="ceo-scope-distributor" />
                <span className="text-sm">By Distributor</span>
              </label>
            </div>
            {filters.scope === "franchise" && (
              <div className="mt-3">
                <Select value={filters.franchise_id} onValueChange={(v) => setFilters({ ...filters, franchise_id: v })}>
                  <SelectTrigger className="h-10 bg-white border-[#0D1B2A]/15" data-testid="ceo-franchise-select"><SelectValue placeholder="Choose franchise…" /></SelectTrigger>
                  <SelectContent>
                    {franchises.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {filters.scope === "distributor" && (
              <div className="mt-3">
                <Select value={filters.distributor_id} onValueChange={(v) => setFilters({ ...filters, distributor_id: v })}>
                  <SelectTrigger className="h-10 bg-white border-[#0D1B2A]/15" data-testid="ceo-distributor-select"><SelectValue placeholder="Choose distributor…" /></SelectTrigger>
                  <SelectContent>
                    {distributors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Stock Type</div>
            <div className="flex flex-wrap gap-4">
              {[["both", "Both"], ["memo", "Memo Only"], ["sold", "Sold Only"]].map(([v, label]) => (
                <label key={v} className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="stock_type" value={v} checked={filters.stock_type === v}
                    onChange={() => setFilters({ ...filters, stock_type: v })}
                    data-testid={`ceo-stocktype-${v}`} />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 pt-2">
          <Button onClick={generate} disabled={loading} className="bg-[#0D1B2A] text-white hover:bg-[#0D1B2A]/90" data-testid="ceo-generate-btn">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> : "Generate Report"}
          </Button>
          <Button onClick={exportXlsx} disabled={exporting || !report || (s && s.total_pieces === 0)}
            variant="outline" className="border-[#0D1B2A]/15" data-testid="ceo-export-btn">
            {exporting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Exporting…</> : <><FileSpreadsheet className="w-4 h-4 mr-2" /> Export to Excel</>}
          </Button>
        </div>
      </div>

      {report && s && s.total_pieces === 0 && (
        <div className="luxe-card p-8 text-center text-slate-500" data-testid="ceo-empty-state">
          No products match the selected filters.
        </div>
      )}

      {report && s && s.total_pieces > 0 && (
        <>
          {/* Section 1: KPI cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="ceo-kpi-section">
            <KpiCard testid="ceo-kpi-pieces" icon={Package} label="Total Pieces"
              value={formatIndian(s.total_pieces)}
              sublines={[`${formatIndian(s.memo_pieces)} memo · ${formatIndian(s.sold_pieces)} sold`]} />
            <KpiCard testid="ceo-kpi-gold" icon={Coins} label="Total Gold Weight"
              value={`${formatIndian(s.total_net_weight_g, { decimals: 2 })} g`}
              sublines={[purityLine]} />
            <KpiCard testid="ceo-kpi-diamond" icon={Gem} label="Total Diamond Weight"
              value={`${formatIndian(s.total_diamond_weight_ct, { decimals: 2 })} ct`}
              sublines={[`${formatIndian(s.total_diamond_pieces)} pcs`]} />
            <KpiCard testid="ceo-kpi-value" icon={TrendingUp} label="Total Stock Value" accent
              value={rupees(s.customer_total)}
              sublines={[
                `Franchise: ${rupees(s.franchise_total)}`,
                `Company: ${rupees(s.company_total)}`,
                `Company GP: ${rupees(s.company_gp)} (${s.company_gp_pct.toFixed(1)}%)`,
              ]} />
          </div>

          {/* Section 2 */}
          <StockValueTable vs={report.value_summary} />

          {/* Section 3 */}
          <BreakdownTable title="Category-wise Breakdown" rows={report.by_category}
            keyName="category" keyLabel="Category"
            testid="ceo-by-category"
            defaultSort={{ key: "company_cost", dir: "desc" }} />

          {/* Section 4 */}
          <BreakdownTable title="Purity-wise Breakdown" rows={report.by_purity}
            keyName="purity" keyLabel="Purity"
            testid="ceo-by-purity"
            defaultSort={{ key: "customer_mrp", dir: "desc" }} />

          {/* Section 5 — only when scope=all */}
          {filters.scope === "all" && report.by_franchise.length > 0 && (
            <BreakdownTable title="Source / Franchise Breakdown" rows={report.by_franchise}
              keyName="franchise" keyLabel="Franchise"
              includeFranchiseCols
              testid="ceo-by-franchise"
              defaultSort={{ key: "company_cost", dir: "desc" }} />
          )}

          <p className="text-xs text-slate-400 px-2" data-testid="ceo-rate-footnote">
            Calculated using Gold {rupees(report.rate_meta.gold_24kt)}/g · GST {(report.rate_meta.gst_rate * 100).toFixed(2)}% · Rate version {report.rate_meta.version} · Generated by {report.generated_by} on {new Date(report.generated_at).toLocaleString()}.
          </p>
        </>
      )}
    </div>
  );
}
